import { ApprovalFlowStep } from '@app/components/approval-status-flow/approval-status-flow.component';
import { OfferLetter } from '@app/models/offer-letters/offer-letter.model';
import { AppDatePipe } from '@app/pipes';
import { ApprovalFlowStatus } from '@app/enums';
import { Candidate } from '@app/models/offer-letters/candidate.model';
import { Employee } from '@app/models/employee/employee.model';

const appDate = new AppDatePipe();

export function getOfferLetterApprovalFlowSteps(candidate: Candidate, offerLetter: OfferLetter): ApprovalFlowStep[] {
    const offerLetterApprovalFlowSteps: ApprovalFlowStep[] = [];

    offerLetterApprovalFlowSteps.push(getFirstStep(offerLetter));

    /**
     * Offer Letter Seen Step
     * We show this step if they've seen the offer letter,
     * or have the ability to see it in the future
     */
    if (candidate.hasSeen() || (!candidate.isHired() && offerLetter.isPending())) {
        offerLetterApprovalFlowSteps.push(getOfferLetterSeenStep(candidate));
    }

    /**
     * Candidate Response Step
     * We want to show this step if the candidate is not hired, or they are hired and this step was acted upon (accepted, declined, withdrawn)
     */
    if (shouldShowCandidateResponseStep(candidate, offerLetter)) {
        offerLetterApprovalFlowSteps.push(getCandidateResponseStep(candidate, offerLetter));
    }

    /**
     * Candidate Hired Step
     * We want to show this step if the candidate is hired
     * or if the letter is pending or accepted
     */
    if (showCandidateHiredStep(candidate, offerLetter)) {
        offerLetterApprovalFlowSteps.push(getCandidateHiredStep(candidate));
    }

    return offerLetterApprovalFlowSteps;
}

/**
 * getFirstStep generates the first step, which will be different based
 * on whether the candidate has been sent the offer letter.
 */
function getFirstStep(offerLetter: OfferLetter): ApprovalFlowStep {
    const sentDateString = appDate.transform(offerLetter.sentAt, 'MMMM d, y');

    // we've sent it
    if (sentDateString) {
        return {
            title: 'offer-letter.offerLetterSentToCandidate',
            description: {
                key: 'offer-letter.offerLetterSentBy',
                params: {
                    sender: offerLetter.sender.fullName,
                    date: sentDateString,
                },
            },
            status: ApprovalFlowStatus.PositiveStatus,
            hideIndicator: false,
        };
    }

    // we haven't sent the offer letter yet
    return {
        title: 'offer-letter.offerLetterSentToCandidate',
        description: 'offer-letter.offerLetterPending',
        status: undefined,
        hideIndicator: false,
    };
}

function getOfferLetterSeenStep(candidate: Candidate): ApprovalFlowStep {
    if (candidate.hasSeen()) {
        const seenDateString = appDate.transform(candidate.firstSeenAt, 'MMMM d, y');

        return {
            title: 'offer-letter.offerLetterSeen',
            description: { key: 'offer-letter.offerLetterSeenBy', params: { date: seenDateString } },
            status: ApprovalFlowStatus.PositiveStatus,
            hideIndicator: false,
        };
    }

    return {
        title: 'offer-letter.offerLetterSeen',
        description: 'offer-letter.offerLetterPending',
        status: null,
        hideIndicator: false,
    };
}

function getCandidateResponseStep(candidate: Candidate, offerLetter: OfferLetter): ApprovalFlowStep {
    const candidateResponseStep: ApprovalFlowStep = {
        title: 'offer-letter.offerLetterCandidateResponse',
        description: 'offer-letter.offerLetterPending',
        status: null,
        hideIndicator: candidate.isHired() && offerLetter.isPending(),
    };

    if (offerLetter.isPending()) {
        return candidateResponseStep;
    }

    // Withdrawn
    if (offerLetter.isWithdrawn()) {
        candidateResponseStep.description = {
            key: 'offer-letter.offerLetterWithdrawnOn',
            params: { date: appDate.transform(offerLetter.withdrawnAt, 'MMMM d, y') },
        };
        candidateResponseStep.status = ApprovalFlowStatus.NegativeStatus;
        return candidateResponseStep;
    }

    // Declined
    if (offerLetter.isDeclined()) {
        candidateResponseStep.description = {
            key: 'offer-letter.offerLetterDeclinedOn',
            params: { date: appDate.transform(offerLetter.declinedAt, 'MMMM d, y') },
        };
        candidateResponseStep.status = ApprovalFlowStatus.NegativeStatus;
        return candidateResponseStep;
    }

    // Accepted
    if (offerLetter.isAccepted()) {
        candidateResponseStep.description = {
            key: 'offer-letter.offerLetterAcceptedOn',
            params: { date: appDate.transform(offerLetter.signedAt, 'MMMM d, y') },
        };
        candidateResponseStep.status = ApprovalFlowStatus.PositiveStatus;
        return candidateResponseStep;
    }

    // candidate did not respond
    // hired, but never accepted
    if (candidate.isHired()) {
        candidateResponseStep.description = 'offer-letter.offerLetterNoResponse';
        return candidateResponseStep;
    }

    // Expired
    if (offerLetter.isExpired()) {
        candidateResponseStep.description = {
            key: 'offer-letter.offerLetterExpiredOn',
            params: { date: appDate.transform(offerLetter.expiresOn, 'MMMM d, y') },
        };
        candidateResponseStep.status = ApprovalFlowStatus.NegativeStatus;
        return candidateResponseStep;
    }
}

function getCandidateHiredStep(candidate: Candidate): ApprovalFlowStep {
    const hiredAtString = appDate.transform(candidate.hiredAt, 'MMMM d, y');

    if (candidate.isHired() && hiredAtString) {
        const employee = candidate.employee;

        // candidates hired before we started recording employee creator will not have a creator
        const creator: Employee | null | undefined = employee?.creator;

        return {
            title: 'offer-letter.offerLetterHired',
            description: {
                key: creator ? 'offer-letter.offerLetterHiredBy' : 'offer-letter.offerLetterHiredOn',
                params: {
                    appointee: creator ? creator.fullName : '',
                    date: hiredAtString,
                },
            },
            status: ApprovalFlowStatus.PositiveStatus,
            hideIndicator: false,
        };
    }

    return {
        title: 'offer-letter.offerLetterHired',
        description: '',
        status: undefined,
        hideIndicator: false,
    };
}

/**
 * We want to show this step if the candidate is not hired, or they are hired and this step was acted upon (accepted, declined, withdrawn)
 */
function shouldShowCandidateResponseStep(candidate: Candidate, offerLetter: OfferLetter): boolean {
    if (!candidate.isHired()) {
        return true;
    }

    return offerLetter.isAccepted() || offerLetter.isDeclined() || offerLetter.isWithdrawn();
}

/**
 * We want to show this step if the candidate is hired
 * or if the letter is pending or accepted
 */
function showCandidateHiredStep(candidate: Candidate, offerLetter: OfferLetter): boolean {
    if (candidate.isHired()) {
        return true;
    }

    return offerLetter.isPending() || offerLetter.isAccepted();
}
